import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '../lib/api';
import { resolveReturn } from '../utils/returnNav';
import { fmtMoney, fmtPrice } from '../utils/currency';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { ArrowLeft, Edit, FileText, DollarSign, User, Plus, Trash2, Link2, X, Download, CreditCard, ArrowDownRight, ArrowUpRight, Clock, Send, Loader2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatDateOnly } from '../utils/timezone';

export default function InvoiceDetailPage() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const backTo = resolveReturn(searchParams.get('from'));
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [linkedTickets, setLinkedTickets] = useState([]);
  const [allTickets, setAllTickets] = useState([]);
  const [editForm, setEditForm] = useState({
    customer_id: '', issue_date: '', due_date: '', status: 'draft',
    line_items: [{ description: '', quantity: 1, unit_price: 0, amount: 0, tax_exempt: false }],
    notes: ''
  });
  const [syncingWhmcs, setSyncingWhmcs] = useState(false);

  useEffect(() => {
    loadInvoice();
    loadCustomers();
    loadLinkedTickets();
    loadAllTickets();
  }, [invoiceId]);

  const loadCustomers = async () => {
    try { const r = await api.get('/customers', { params: { limit: 10000, status: 'active' } }); const cd = r.data; setCustomers(Array.isArray(cd) ? cd : cd.customers || []); } catch (e) {}
  };

  const loadLinkedTickets = async () => {
    try {
      const response = await api.get(`/invoices/${invoiceId}/tickets`);
      setLinkedTickets(response.data);
    } catch { setLinkedTickets([]); }
  };

  const loadAllTickets = async () => {
    try {
      const response = await api.get('/tickets', { params: { limit: 5000 } });
      const d = response.data;
      setAllTickets(Array.isArray(d) ? d : d.tickets || []);
    } catch { setAllTickets([]); }
  };

  const handleAddLinkedTicket = async (ticketId) => {
    try {
      await api.post(`/invoices/${invoiceId}/tickets`, { ticket_id: ticketId });
      toast.success('Ticket linked');
      loadLinkedTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link ticket');
    }
  };

  const handleRemoveLinkedTicket = async (ticketId) => {
    try {
      await api.delete(`/invoices/${invoiceId}/tickets/${ticketId}`);
      toast.success('Ticket unlinked');
      loadLinkedTickets();
    } catch { toast.error('Failed to unlink ticket'); }
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await api.get(`/export/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoice?.invoice_number || invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to export PDF'); }
  };

  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const handlePreviewPdf = async () => {
    try {
      const response = await api.get(`/export/invoices/${invoiceId}/pdf?preview=true`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPreviewUrl(url);
      setShowPreview(true);
    } catch { toast.error('Failed to load preview'); }
  };

  const [resending, setResending] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ amount: '', method: 'ACH', overpayment_action: 'credit' });

  const balanceDue = Math.max((invoice?.total || 0) - (invoice?.credits_applied || 0), 0);

  const handleRecordPayment = async () => {
    const amt = parseFloat(paymentForm.amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    
    const overpayment = Math.round((amt - balanceDue) * 100) / 100;
    let action = paymentForm.overpayment_action;
    
    if (overpayment > 0) {
      const choice = window.confirm(
        `Payment of $${amt.toFixed(2)} exceeds the balance due of $${balanceDue.toFixed(2)} by $${overpayment.toFixed(2)}.\n\nClick OK to apply the overpayment as customer credit.\nClick Cancel to mark it as a refund.`
      );
      action = choice ? 'credit' : 'refund';
    }
    
    try {
      await api.post('/invoices/bulk-action', {
        invoice_ids: [invoice.id],
        action: 'mark_paid',
        payment_amount: amt,
        payment_method: paymentForm.method,
        overpayment_action: action,
      });
      toast.success(overpayment > 0 
        ? `Payment recorded. $${overpayment.toFixed(2)} ${action === 'credit' ? 'applied as customer credit' : 'marked for refund'}.`
        : 'Payment recorded');
      setPaymentOpen(false);
      setPaymentForm({ amount: '', method: 'ACH', overpayment_action: 'credit' });
      loadInvoice();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to record payment'); }
  };
  const handleResendInvoice = async () => {
    setResending(true);
    try {
      await api.post(`/invoices/bulk-action`, {
        invoice_ids: [invoiceId],
        action: 'mark_sent',
      });
      toast.success('Invoice resent to customer');
      loadInvoice();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend invoice');
    }
    setResending(false);
  };

  const loadInvoice = async () => {
    try {
      const response = await api.get(`/invoices/${invoiceId}`);
      setInvoice(response.data);
    } catch (error) {
      toast.error('Failed to load invoice');
      navigate('/invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.put(`/invoices/${invoiceId}`, { status: newStatus });
      toast.success(`Invoice status updated to ${newStatus}`);
      setInvoice(prev => ({ ...prev, status: newStatus }));
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleSyncWhmcsTransactions = async () => {
    setSyncingWhmcs(true);
    try {
      const response = await api.post(`/invoices/${invoiceId}/sync-whmcs-transactions`);
      const data = response.data;
      toast.success(`Synced ${data.transactions_count} transaction(s) from WHMCS`);
      loadInvoice();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync from WHMCS');
    } finally {
      setSyncingWhmcs(false);
    }
  };

  const openEditDialog = () => {
    if (!invoice) return;
    setEditForm({
      customer_id: invoice.customer_id || '',
      issue_date: invoice.issue_date?.split('T')[0] || '',
      due_date: invoice.due_date?.split('T')[0] || '',
      status: invoice.status || 'draft',
      line_items: invoice.line_items?.length > 0
        ? invoice.line_items.map(li => ({ ...li, quantity: li.quantity || 1, unit_price: li.unit_price || 0, amount: li.amount || 0, tax_exempt: li.tax_exempt || false }))
        : [{ description: '', quantity: 1, unit_price: 0, amount: 0, tax_exempt: false }],
      notes: invoice.notes || ''
    });
    setEditDialogOpen(true);
  };

  const updateLineItem = (index, field, value) => {
    const items = [...editForm.line_items];
    items[index][field] = value;
    if (field === 'quantity' || field === 'unit_price') {
      items[index].amount = (parseFloat(items[index].quantity) || 0) * (parseFloat(items[index].unit_price) || 0);
    }
    setEditForm({ ...editForm, line_items: items });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      const customer = customers.find(c => c.id === editForm.customer_id);
      const subtotal = editForm.line_items.reduce((s, i) => s + (i.amount || 0), 0);
      const taxable = editForm.line_items.filter(i => !i.tax_exempt).reduce((s, i) => s + (i.amount || 0), 0);
      const taxAmount = taxable * 0.0825;
      await api.put(`/invoices/${invoiceId}`, {
        ...editForm,
        customer_name: customer?.company_name || invoice.customer_name,
        subtotal, tax_rate: 0.0825, tax_amount: taxAmount, total: subtotal + taxAmount,
      });
      toast.success('Invoice updated');
      setEditDialogOpen(false);
      loadInvoice();
    } catch (error) {
      toast.error('Failed to update invoice');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      paid: 'bg-emerald-100 text-emerald-800',
      overdue: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || colors.draft;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try { return formatDateOnly(dateStr, 'MMM d, yyyy'); } catch { return dateStr; }
  };

  if (loading) {
    return (
      <div className="co-page co-scope co-settings flex flex-col items-center justify-center min-h-[60vh]">
        <img src="/cipherone-logo.png" alt="Loading" className="h-16 w-16 logo-spin mb-4" />
        <div className="text-lg text-gray-600">Loading invoice...</div>
      </div>
    );
  }

  if (!invoice) return null;

  return (
    <div className="co-page co-scope co-settings space-y-6" data-testid="invoice-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate(backTo.to)} data-testid="back-to-invoices">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {backTo.label}
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FileText className="h-6 w-6 text-neutral-500" />
              Invoice {invoice.invoice_number}
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="custom" className={`${getStatusColor(invoice.status)} text-sm px-3 py-1`}>
            {invoice.status?.charAt(0).toUpperCase() + invoice.status?.slice(1)}
          </Badge>
          <Select value={invoice.status} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-[130px] h-9" data-testid="invoice-status-change">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
            <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => { setPaymentForm(p => ({ ...p, amount: balanceDue.toFixed(2) })); setPaymentOpen(true); }} data-testid="record-payment-btn">
              <DollarSign className="h-4 w-4 mr-1" /> Record Payment
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={openEditDialog} data-testid="edit-invoice-btn">
            <Edit className="h-4 w-4 mr-2" />
            Edit Invoice
          </Button>
          {invoice.integration_data?.whmcs_invoice_id && (
            <Button variant="outline" size="sm" onClick={handleSyncWhmcsTransactions} disabled={syncingWhmcs} data-testid="sync-whmcs-txns-btn">
              {syncingWhmcs ? (
                <span className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-neutral-300 border-t-neutral-600 inline-block" />
              ) : (
                <CreditCard className="h-4 w-4 mr-2" />
              )}
              {syncingWhmcs ? 'Syncing...' : 'Sync WHMCS'}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handlePreviewPdf} data-testid="preview-invoice-pdf-btn">
            <Eye className="h-4 w-4 mr-2" />
            Preview
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownloadPdf} data-testid="export-invoice-pdf-btn">
            <Download className="h-4 w-4 mr-2" />
            PDF
          </Button>
          <Button variant="outline" size="sm" onClick={handleResendInvoice} disabled={resending} data-testid="resend-invoice-btn">
            {resending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Resend
          </Button>
        </div>
      </div>

      {/* Invoice Details */}
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <Label className="text-xs text-neutral-500 uppercase tracking-wide">Customer</Label>
              {invoice.customer_id ? (
                <Link
                  to={`/customers/${invoice.customer_id}`}
                  className="font-semibold text-blue-600 hover:text-blue-800 hover:underline mt-1 flex items-center gap-1"
                  data-testid="invoice-detail-customer-link"
                >
                  <User className="h-4 w-4" />
                  {invoice.customer_name || 'View Customer'}
                </Link>
              ) : (
                <p className="font-semibold text-neutral-800 mt-1">{invoice.customer_name || '-'}</p>
              )}
            </div>
            <div>
              <Label className="text-xs text-neutral-500 uppercase tracking-wide">Invoice Number</Label>
              <p className="font-mono font-semibold text-neutral-800 mt-1">{invoice.invoice_number}</p>
            </div>
            <div>
              <Label className="text-xs text-neutral-500 uppercase tracking-wide">Issue Date</Label>
              <p className="text-neutral-800 mt-1">{formatDate(invoice.issue_date)}</p>
            </div>
            <div>
              <Label className="text-xs text-neutral-500 uppercase tracking-wide">Due Date</Label>
              <p className="text-neutral-800 mt-1">{formatDate(invoice.due_date)}</p>
            </div>
          </div>

          {/* WHMCS Reference */}
          {invoice.integration_data?.whmcs_invoice_id && (
            <div className="mt-4 pt-4 border-t">
              <span className="text-xs text-neutral-500">WHMCS Invoice ID: </span>
              <span className="font-mono text-sm text-neutral-600">{invoice.integration_data.whmcs_invoice_id}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Linked Tickets */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-neutral-500" />
              Linked Tickets
            </span>
            <Select value="" onValueChange={handleAddLinkedTicket}>
              <SelectTrigger className="w-[220px] h-8 text-xs" data-testid="link-ticket-select">
                <SelectValue placeholder="Link a ticket..." />
              </SelectTrigger>
              <SelectContent>
                {allTickets.filter(t => !linkedTickets.find(lt => lt.ticket_id === t.id) && (!invoice?.customer_id || t.customer_id === invoice.customer_id)).map(t => (
                  <SelectItem key={t.id} value={t.id}>
                    #{t.ticket_number} - {t.title?.substring(0, 40)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardTitle>
        </CardHeader>
        {linkedTickets.length > 0 && (
          <CardContent>
            <div className="space-y-2">
              {linkedTickets.map(lt => (
                <div key={lt.ticket_id} className="flex items-center justify-between p-2 rounded-lg bg-neutral-50 hover:bg-neutral-100 transition-colors" data-testid={`linked-ticket-${lt.ticket_id}`}>
                  <div 
                    className="flex items-center gap-2 cursor-pointer flex-1"
                    onClick={() => navigate(`/tickets/${lt.ticket_id}`)}
                  >
                    <Badge variant="outline" className="text-xs font-mono">#{lt.ticket_number}</Badge>
                    <span className="text-sm font-medium text-neutral-800">{lt.title}</span>
                    {lt.status && (
                      <Badge variant="custom" className={`text-xs ${lt.status === 'open' ? 'bg-blue-100 text-blue-800' : lt.status === 'closed' ? 'bg-gray-100 text-gray-600' : 'bg-amber-100 text-amber-800'}`}>
                        {lt.status}
                      </Badge>
                    )}
                  </div>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleRemoveLinkedTicket(lt.ticket_id)} data-testid={`unlink-ticket-${lt.ticket_id}`}>
                    <X className="h-3.5 w-3.5 text-neutral-400 hover:text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Line Items */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-neutral-500" />
            Line Items
          </CardTitle>
        </CardHeader>
        <CardContent>
          {invoice.line_items?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50%]">Description</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Unit Price</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-center">Tax</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoice.line_items.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="text-sm">{item.description}</TableCell>
                    <TableCell className="text-right">{item.quantity}</TableCell>
                    <TableCell className="text-right">${fmtPrice(item.unit_price || 0)}</TableCell>
                    <TableCell className="text-right font-semibold">${fmtMoney(item.amount || 0)}</TableCell>
                    <TableCell className="text-center">
                      {item.tax_exempt
                        ? <Badge variant="custom" className="bg-gray-100 text-gray-700 text-xs">Non-Taxable</Badge>
                        : <Badge variant="custom" className="bg-emerald-50 text-emerald-700 text-xs">Taxable</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-neutral-500">
              <FileText className="h-10 w-10 mx-auto text-neutral-300 mb-2" />
              <p>No line items on this invoice</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Totals */}
      <Card>
        <CardContent className="p-6">
          <div className="max-w-xs ml-auto space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-neutral-600">Subtotal:</span>
              <span className="font-medium">${fmtMoney(invoice.subtotal || 0)}</span>
            </div>
            {(invoice.tax_amount || 0) > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-neutral-600">Tax:</span>
                <span className="font-medium">${fmtMoney(invoice.tax_amount || 0)}</span>
              </div>
            )}
            {(invoice.integration_data?.whmcs_credit || 0) > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-emerald-600">Credit Applied (WHMCS):</span>
                <span className="font-medium text-emerald-600">-${fmtMoney(invoice.integration_data.whmcs_credit || 0)}</span>
              </div>
            )}
            {(invoice.credits_applied || 0) > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-emerald-600">Credits Applied:</span>
                <span className="font-medium text-emerald-600">-${fmtMoney(invoice.credits_applied || 0)}</span>
              </div>
            )}
            <Separator />
            <div className="flex justify-between text-xl font-bold">
              <span>Total:</span>
              <span>${fmtMoney(invoice.total || 0)}</span>
            </div>
            {((invoice.credits_applied || 0) > 0 || (invoice.integration_data?.whmcs_credit || 0) > 0) && (
              <div className="flex justify-between text-lg font-bold text-blue-700 bg-blue-50 rounded px-2 py-1 -mx-2">
                <span>Balance Due:</span>
                <span>${fmtMoney(Math.max((invoice.total || 0) - (invoice.credits_applied || 0) - (invoice.integration_data?.whmcs_credit || 0), 0))}</span>
              </div>
            )}
            {(invoice.integration_data?.whmcs_balance != null && invoice.integration_data?.whmcs_invoice_id) && (
              <div className="flex justify-between text-sm">
                <span className="text-neutral-500">Balance Due (WHMCS):</span>
                <span className={`font-semibold ${parseFloat(invoice.integration_data.whmcs_balance) > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                  ${fmtMoney(invoice.integration_data.whmcs_balance || 0)}
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* WHMCS Transactions */}
      {invoice.integration_data?.whmcs_transactions?.length > 0 && (
        <Card data-testid="whmcs-transactions-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-neutral-500" />
              Transaction History
              <Badge variant="outline" className="ml-2 text-xs font-normal">WHMCS</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Desktop Table */}
            <div className="hidden sm:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Gateway</TableHead>
                    <TableHead>Transaction ID</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Amount In</TableHead>
                    <TableHead className="text-right">Amount Out</TableHead>
                    <TableHead className="text-right">Fees</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoice.integration_data.whmcs_transactions.map((txn, idx) => (
                    <TableRow key={txn.id || idx} data-testid={`whmcs-txn-row-${idx}`}>
                      <TableCell className="text-sm whitespace-nowrap">{txn.date || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs capitalize">{txn.gateway || '-'}</Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-neutral-500 max-w-[140px] truncate" title={txn.transaction_id}>
                        {txn.transaction_id || '-'}
                      </TableCell>
                      <TableCell className="text-sm max-w-[200px] truncate" title={txn.description}>
                        {txn.description || '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        {txn.amount_in > 0 ? (
                          <span className="text-emerald-600 font-medium flex items-center justify-end gap-1">
                            <ArrowDownRight className="h-3 w-3" />
                            ${fmtMoney(txn.amount_in)}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        {txn.amount_out > 0 ? (
                          <span className="text-red-600 font-medium flex items-center justify-end gap-1">
                            <ArrowUpRight className="h-3 w-3" />
                            ${fmtMoney(txn.amount_out)}
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-right text-sm text-neutral-500">
                        {txn.fees > 0 ? `$${fmtMoney(txn.fees)}` : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Card Layout */}
            <div className="sm:hidden space-y-3">
              {invoice.integration_data.whmcs_transactions.map((txn, idx) => (
                <div key={txn.id || idx} className="p-3 rounded-lg border bg-neutral-50 space-y-2" data-testid={`whmcs-txn-card-${idx}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-neutral-500 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {txn.date || '-'}
                    </span>
                    <Badge variant="outline" className="text-xs capitalize">{txn.gateway || '-'}</Badge>
                  </div>
                  {txn.description && (
                    <p className="text-sm text-neutral-700 truncate">{txn.description}</p>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      {txn.amount_in > 0 && (
                        <span className="text-emerald-600 font-semibold text-sm flex items-center gap-1">
                          <ArrowDownRight className="h-3 w-3" />
                          In: ${fmtMoney(txn.amount_in)}
                        </span>
                      )}
                      {txn.amount_out > 0 && (
                        <span className="text-red-600 font-semibold text-sm flex items-center gap-1">
                          <ArrowUpRight className="h-3 w-3" />
                          Out: ${fmtMoney(txn.amount_out)}
                        </span>
                      )}
                    </div>
                    {txn.fees > 0 && (
                      <span className="text-xs text-neutral-400">Fee: ${fmtMoney(txn.fees)}</span>
                    )}
                  </div>
                  {txn.transaction_id && (
                    <p className="font-mono text-[10px] text-neutral-400 truncate">TxID: {txn.transaction_id}</p>
                  )}
                </div>
              ))}
            </div>

            {/* Transaction Summary */}
            <div className="mt-4 pt-3 border-t flex flex-wrap gap-4 text-sm">
              <div>
                <span className="text-neutral-500">Total In: </span>
                <span className="font-semibold text-emerald-600">
                  ${fmtMoney(invoice.integration_data.whmcs_transactions.reduce((sum, t) => sum + (t.amount_in || 0), 0))}
                </span>
              </div>
              <div>
                <span className="text-neutral-500">Total Out: </span>
                <span className="font-semibold text-red-600">
                  ${fmtMoney(invoice.integration_data.whmcs_transactions.reduce((sum, t) => sum + (t.amount_out || 0), 0))}
                </span>
              </div>
              <div>
                <span className="text-neutral-500">Total Fees: </span>
                <span className="font-medium text-neutral-600">
                  ${fmtMoney(invoice.integration_data.whmcs_transactions.reduce((sum, t) => sum + (t.fees || 0), 0))}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* WHMCS Payment Method */}
      {invoice.integration_data?.whmcs_payment_method && (
        <div className="flex items-center gap-2 text-sm text-neutral-500">
          <CreditCard className="h-4 w-4" />
          <span>Payment Method: </span>
          <span className="font-medium text-neutral-700 capitalize">{invoice.integration_data.whmcs_payment_method}</span>
        </div>
      )}

      {/* Notes */}
      {invoice.notes && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-neutral-700 whitespace-pre-wrap">{invoice.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Created/Updated Info */}
      <div className="text-xs text-neutral-400 flex gap-4">
        {invoice.created_at && <span>Created: {formatDate(invoice.created_at)}</span>}
        {invoice.updated_at && <span>Updated: {formatDate(invoice.updated_at)}</span>}
        {invoice.source && <span>Source: {invoice.source}</span>}
      </div>

      {/* Edit Invoice Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="co-scope co-settings w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto p-3 sm:p-6" data-testid="edit-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm sm:text-lg">Edit Invoice {invoice.invoice_number}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-3 sm:space-y-4 mt-2">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
              <div>
                <Label className="text-xs sm:text-sm">Customer</Label>
                <Select value={editForm.customer_id} onValueChange={(v) => setEditForm({ ...editForm, customer_id: v })}>
                  <SelectTrigger className="h-8 sm:h-9 text-xs sm:text-sm"><SelectValue placeholder="Select customer" /></SelectTrigger>
                  <SelectContent>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs sm:text-sm">Issue Date</Label>
                <Input type="date" className="h-8 sm:h-9 text-xs sm:text-sm" value={editForm.issue_date} onChange={(e) => setEditForm({ ...editForm, issue_date: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs sm:text-sm">Due Date</Label>
                <Input type="date" className="h-8 sm:h-9 text-xs sm:text-sm" value={editForm.due_date} onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })} />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <Label className="text-xs sm:text-sm">Line Items</Label>
                <Button type="button" variant="outline" size="sm" className="h-7 sm:h-8 text-xs" onClick={() => setEditForm({ ...editForm, line_items: [...editForm.line_items, { description: '', quantity: 1, unit_price: 0, amount: 0, tax_exempt: false }] })}>
                  <Plus className="h-3 w-3 mr-1" /> Add
                </Button>
              </div>
              {/* Mobile: Stacked layout */}
              <div className="sm:hidden space-y-3">
                {editForm.line_items.map((item, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-semibold text-gray-600">Item {index + 1}</span>
                      <Button type="button" variant="ghost" size="sm" onClick={() => {
                        if (editForm.line_items.length > 1) setEditForm({ ...editForm, line_items: editForm.line_items.filter((_, i) => i !== index) });
                      }} className="h-6 w-6 p-0">
                        <Trash2 className="h-3 w-3 text-red-500" />
                      </Button>
                    </div>
                    <Input className="h-8 text-xs" value={item.description} onChange={(e) => updateLineItem(index, 'description', e.target.value)} placeholder="Description" />
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-[10px] text-gray-500">Qty</Label>
                        <Input className="h-7 text-xs" type="number" value={item.quantity} onChange={(e) => updateLineItem(index, 'quantity', e.target.value)} />
                      </div>
                      <div>
                        <Label className="text-[10px] text-gray-500">Price</Label>
                        <Input className="h-7 text-xs" type="number" step="0.01" value={item.unit_price} onChange={(e) => updateLineItem(index, 'unit_price', e.target.value)} />
                      </div>
                      <div>
                        <Label className="text-[10px] text-gray-500">Total</Label>
                        <Input className="h-7 text-xs bg-gray-100" type="number" value={(item.amount || 0).toFixed(2)} readOnly />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox checked={item.tax_exempt} onCheckedChange={(c) => updateLineItem(index, 'tax_exempt', c)} />
                      <span className="text-xs text-gray-600">Non-taxable</span>
                    </div>
                  </div>
                ))}
              </div>
              {/* Desktop: Grid layout */}
              <div className="hidden sm:block">
                <div className="grid grid-cols-12 gap-2 items-center mb-2 px-1">
                  <div className="col-span-5 text-xs font-semibold text-gray-600">Description</div>
                  <div className="col-span-1 text-xs font-semibold text-gray-600">Qty</div>
                  <div className="col-span-2 text-xs font-semibold text-gray-600">Price</div>
                  <div className="col-span-2 text-xs font-semibold text-gray-600">Total</div>
                  <div className="col-span-1 text-xs font-semibold text-gray-600 text-center">NT</div>
                  <div className="col-span-1"></div>
                </div>
                <div className="space-y-2">
                  {editForm.line_items.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-center">
                      <Input className="col-span-5" value={item.description} onChange={(e) => updateLineItem(index, 'description', e.target.value)} placeholder="Description" />
                      <Input className="col-span-1" type="number" value={item.quantity} onChange={(e) => updateLineItem(index, 'quantity', e.target.value)} />
                      <Input className="col-span-2" type="number" step="0.01" value={item.unit_price} onChange={(e) => updateLineItem(index, 'unit_price', e.target.value)} />
                      <Input className="col-span-2" type="number" value={(item.amount || 0).toFixed(2)} readOnly />
                      <div className="col-span-1 flex justify-center">
                        <Checkbox checked={item.tax_exempt} onCheckedChange={(c) => updateLineItem(index, 'tax_exempt', c)} />
                      </div>
                      <Button type="button" variant="ghost" size="sm" onClick={() => {
                        if (editForm.line_items.length > 1) setEditForm({ ...editForm, line_items: editForm.line_items.filter((_, i) => i !== index) });
                      }} className="col-span-1">
                        <Trash2 className="h-3 w-3 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs sm:text-sm">Notes</Label>
              <Textarea className="text-xs sm:text-sm" value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} rows={3} />
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" className="h-8 sm:h-9 text-xs sm:text-sm" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="h-8 sm:h-9 text-xs sm:text-sm">Update Invoice</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={paymentOpen} onOpenChange={setPaymentOpen}>
        <DialogContent className="co-scope co-settings max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment — #{invoice?.invoice_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-gray-50 rounded p-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-gray-600">Invoice Total:</span><span className="font-semibold">${fmtMoney(invoice?.total || 0)}</span></div>
              {(invoice?.credits_applied || 0) > 0 && (
                <div className="flex justify-between text-green-700"><span>Credits Applied:</span><span>-${fmtMoney(invoice?.credits_applied || 0)}</span></div>
              )}
              <div className="flex justify-between font-bold border-t pt-1"><span>Balance Due:</span><span>${fmtMoney(balanceDue)}</span></div>
            </div>
            <div>
              <Label>Payment Amount</Label>
              <Input type="number" step="0.01" value={paymentForm.amount} onChange={e => setPaymentForm(p => ({ ...p, amount: e.target.value }))} placeholder="0.00" className="text-lg font-mono" data-testid="payment-amount-input" />
              {parseFloat(paymentForm.amount) > balanceDue && (
                <p className="text-xs text-amber-600 mt-1">Overpayment of ${(parseFloat(paymentForm.amount) - balanceDue).toFixed(2)} — you'll be asked how to handle it.</p>
              )}
            </div>
            <div>
              <Label>Payment Method</Label>
              <Select value={paymentForm.method} onValueChange={v => setPaymentForm(p => ({ ...p, method: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACH">ACH</SelectItem>
                  <SelectItem value="Check">Check</SelectItem>
                  <SelectItem value="Credit Card">Credit Card</SelectItem>
                  <SelectItem value="Wire Transfer">Wire Transfer</SelectItem>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button className="w-full bg-green-600 hover:bg-green-700" onClick={handleRecordPayment} data-testid="confirm-payment-btn">
              <DollarSign className="h-4 w-4 mr-1" /> Record Payment
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF Preview Modal */}
      <Dialog open={showPreview} onOpenChange={(open) => { if (!open) { setShowPreview(false); if (previewUrl) { window.URL.revokeObjectURL(previewUrl); setPreviewUrl(null); } } }}>
        <DialogContent className="co-scope co-settings max-w-5xl h-[90vh] p-0" data-testid="pdf-preview-dialog">
          <div className="flex items-center justify-between px-4 py-2 border-b bg-gray-50">
            <span className="text-sm font-semibold">Invoice Preview — #{invoice?.invoice_number}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleDownloadPdf}><Download className="h-3.5 w-3.5 mr-1" /> Download</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowPreview(false)}><X className="h-4 w-4" /></Button>
            </div>
          </div>
          {previewUrl && (
            <iframe src={previewUrl} className="w-full flex-1" style={{ height: 'calc(90vh - 50px)', border: 'none' }} title="Invoice PDF Preview" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
